import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { Shield, Eye, EyeOff, Loader2 } from "lucide-react";

export default function SignupPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "", child_name: "", child_age: "" });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await register({ ...form, child_age: parseInt(form.child_age) || 10 });
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#FFF0E0]/50 border-2 border-transparent focus:border-[#4D96FF] focus:bg-white rounded-2xl px-4 py-3 text-base font-semibold outline-none transition-all placeholder:text-[#52526B]/50";

  return (
    <div className="min-h-screen bg-[#FFF8F0] flex font-nunito" data-testid="signup-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/43b86a66-f576-4564-b06d-193403b5349b/images/f2ace498661f24599a2029b74ace8557955807e6821093bc0edf821d16d31a9d.png"
          alt="Alfie"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-10 text-center px-12">
          <div className="inline-flex items-center gap-3 bg-white/90 backdrop-blur-sm rounded-[32px] px-8 py-5 shadow-lg">
            <Shield className="w-10 h-10 text-[#FF5C39]" strokeWidth={3} />
            <span className="text-3xl font-extrabold text-[#1F1F2E]">Alfie</span>
          </div>
          <p className="mt-6 text-lg font-bold text-white bg-[#1F1F2E]/70 backdrop-blur-sm rounded-2xl px-6 py-3 inline-block">
            A safer digital world for your child
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#FF5C39] rounded-2xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" strokeWidth={3} />
            </div>
            <span className="text-2xl font-extrabold text-[#1F1F2E]">Alfie</span>
          </div>

          <h1 className="text-3xl md:text-4xl font-extrabold text-[#1F1F2E] tracking-tight" data-testid="signup-heading">
            Get started
          </h1>
          <p className="text-[#52526B] font-semibold mt-2 mb-6">
            Set up your account and your child's profile
          </p>

          {error && (
            <div className="bg-[#F43F5E]/10 text-[#F43F5E] px-4 py-3 rounded-2xl font-bold text-sm mb-4" data-testid="signup-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#52526B] mb-1 block">Your name</label>
              <input type="text" value={form.name} onChange={update("name")} data-testid="signup-name-input" className={inputCls} placeholder="Your full name" required />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#52526B] mb-1 block">Email</label>
              <input type="email" value={form.email} onChange={update("email")} data-testid="signup-email-input" className={inputCls} placeholder="parent@email.com" required />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#52526B] mb-1 block">Password</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={form.password} onChange={update("password")} data-testid="signup-password-input" className={`${inputCls} pr-12`} placeholder="Min 6 characters" required />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#52526B]">
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="pt-2 pb-1">
              <p className="text-sm font-bold text-[#FF5C39]">About your child</p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-[#52526B] mb-1 block">Child's name</label>
                <input type="text" value={form.child_name} onChange={update("child_name")} data-testid="signup-child-name-input" className={inputCls} placeholder="Child's first name" required />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#52526B] mb-1 block">Age</label>
                <input type="number" min="4" max="17" value={form.child_age} onChange={update("child_age")} data-testid="signup-child-age-input" className={inputCls} placeholder="10" required />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="signup-submit-button"
              className="w-full bg-[#FF5C39] text-white font-bold rounded-2xl px-6 py-4 hover:bg-[#E04828] active:scale-95 transition-all shadow-[0_8px_20px_rgba(255,92,57,0.25)] disabled:opacity-50 flex items-center justify-center gap-2 mt-4"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              Create account
            </button>
          </form>

          <p className="mt-6 text-center text-[#52526B] font-semibold text-sm">
            Already have an account?{" "}
            <Link to="/login" data-testid="login-link" className="text-[#FF5C39] font-bold hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
